import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { typedApi } from '../api/typed';
import {
  CalendarDays, Inbox, Phone, Calendar, BookOpen, FileText,
  CreditCard, X, Sparkles, Terminal, Layers, Users, Handshake, ListTodo, Flame,
  PanelLeftClose, PanelLeftOpen, Settings
} from 'lucide-react';
import { DraftIndicator } from './DraftIndicator';
import { DailyBriefCard } from './sidebar/DailyBriefCard';
import { SidebarSavedViews } from './sidebar/SidebarSavedViews';
import SidebarNavItem from './sidebar/SidebarNavItem';
import { SessionHealthBadge } from './sidebar/SessionHealthBadge';
import { SidebarBusinessFilter } from './sidebar/SidebarBusinessFilter';
import { SidebarMoreSection } from './sidebar/SidebarMoreSection';
import { BrandMark } from './BrandMark';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';

// Primary nav — daily drivers, always visible.
const NAV_SECTIONS = [
  { label: 'Desk', items: [
    { path: '/', label: 'Today', icon: CalendarDays, note: 'Command centre', countKey: 'tasks', badgeKind: 'action' },
    { path: '/briefing', label: 'Hot on the Plate', icon: Flame, note: 'Biting next 12h', badgeKind: 'action' },
    { path: '/tasks', label: 'Tasks', icon: ListTodo, note: 'All & pending', countKey: 'tasks', badgeKind: 'action' },
    { path: '/calls', label: 'Calls', icon: Phone, note: 'Queue', countKey: 'calls', badgeKind: 'action' },
    { path: '/reading', label: 'Reading', icon: BookOpen, note: 'Research', countKey: 'reading', badgeKind: 'passive' },
    { path: '/notes', label: 'Notes', icon: FileText, note: 'Context', badgeKind: 'passive' },
    { path: '/payments', label: 'Payments', icon: CreditCard, note: 'Due board', countKey: 'payments', urgentKey: 'paymentsOverdue', badgeKind: 'action' },
  ]},
  { label: 'Plan', items: [
    { path: '/calendar', label: 'Calendar', icon: Calendar, note: 'Time grid' },
    { path: '/inbox', label: 'Inbox', icon: Inbox, note: 'Triage', countKey: 'inbox', badgeKind: 'passive' },
    { path: '/commitments', label: 'Commitments', icon: Handshake, note: 'Obligations', countKey: 'commitments', badgeKind: 'tracking' },
    { path: '/delegatables', label: 'Delegatables', icon: Users, note: 'Your team', countKey: 'delegated', badgeKind: 'tracking' },
  ]},
  { label: 'Systems', items: [
    { path: '/hub', label: 'PolkTaskHub', icon: Terminal, note: 'Capture & parse' },
    { path: '/templates', label: 'Templates', icon: Layers, note: 'Task patterns' },
  ]},
];

function Sidebar({ mobileOpen, onMobileClose }) {
  const [expanded, setExpanded] = useState(() => {
    try { return safeStorage.getString('polkdesq_sidebar_expanded') !== 'false'; } catch { return true; }
  });
  const [moreOpen, setMoreOpen] = useState(() => {
    try { return safeStorage.getString('polkdesq_sidebar_more_open') === 'true'; } catch { return false; }
  });
  const [isMobile, setIsMobile] = useState(false);
  const [counts, setCounts] = useState({});
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [, setTick] = useState(0); // force re-render for relative time
  const navigate = useNavigate();
  const location = useLocation();
  const { businesses, activeBusiness, setActiveBusiness } = useBusiness();
  const { sessionMeta } = useAuth();

  const fetchCounts = useCallback(async (signal) => {
    try {
      const data = await typedApi.get('/api/counts', { signal });
      setCounts(data);
      setLastSyncTime(new Date());
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      logger.warn('Sidebar counts fetch failed (badges stale until next poll):', err?.message);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchCounts(controller.signal);
    const interval = setInterval(() => {
      // each tick uses a fresh controller so we can cancel the *pending* poll on unmount
      fetchCounts(controller.signal);
    }, 30000);
    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [fetchCounts]);

  // Tick every 15s to update "synced X ago" text
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleExpanded = (val) => {
    setExpanded(val);
    try { safeStorage.setString('polkdesq_sidebar_expanded', String(val)); } catch (err) { logger.warn('Sidebar expanded state not persisted:', err?.message); }
  };

  const toggleMore = () => {
    const next = !moreOpen;
    setMoreOpen(next);
    try { safeStorage.setString('polkdesq_sidebar_more_open', String(next)); } catch (err) { logger.warn('Sidebar more-open state not persisted:', err?.message); }
  };

  const showExpanded = isMobile ? true : expanded;
  const sidebarWidth = isMobile ? (mobileOpen ? 292 : 0) : (expanded ? 280 : 92);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) onMobileClose?.();
  };

  return (
    <>
      {isMobile && mobileOpen ? <div data-testid="mobile-more-drawer-overlay" className="mobile-sidebar-overlay" onClick={onMobileClose} /> : null}
      <aside
        data-testid="sidebar"
        role="navigation"
        aria-label="App navigation sidebar"
        data-mobile-drawer-open={isMobile && mobileOpen ? 'true' : undefined}
        className={`sidebar-container pd-sidebar ${isMobile && mobileOpen ? 'mobile-open' : ''} ${showExpanded ? 'is-open' : 'is-collapsed'}`}
        style={{ width: sidebarWidth }}
      >
        <div className="pd-sidebar-panel" {...(isMobile && mobileOpen ? { 'data-testid': 'mobile-more-drawer', role: 'dialog', 'aria-label': 'More navigation drawer' } : {})}>
          <div className="pd-sidebar-brand">
            <BrandMark variant="mark" size="md" testId="sidebar-brand-mark" />
            {showExpanded ? (
              <div className="sidebar-label flex-1 min-w-0">
                <div className="pd-brand-wordmark" style={{ fontSize: 15 }}>
                  PolkDes<span className="pd-brand-wordmark-q">Q</span>
                </div>
                <div className="text-[11px] leading-relaxed mt-1" style={{ color: 'var(--pd-subtle)' }}>अनुशासनं सफलता मूलम्</div>
                <div className="text-[9px] italic" style={{ color: 'var(--pd-subtle)', opacity: 0.7 }}>Anushasanam saphalta mulam</div>
                <div className="text-[9px]" style={{ color: 'var(--pd-subtle)', opacity: 0.55 }}>Discipline is the root of success</div>
              </div>
            ) : null}
            {isMobile && mobileOpen ? (
              <button data-testid="sidebar-close-button" className="ml-auto p-2 rounded-xl hover:bg-black/[0.04]" onClick={onMobileClose}>
                <X className="w-4.5 h-4.5" style={{ color: 'var(--pd-subtle)' }} />
              </button>
            ) : null}
          </div>

          <div className="px-3 pb-2">
            {showExpanded ? (
              <DailyBriefCard />
            ) : (
              <div data-testid="sidebar-assistant-card-collapsed" className="rounded-2xl py-3 px-0 pd-glass flex items-center justify-center">
                <Sparkles className="w-4.5 h-4.5" style={{ color: 'var(--pd-nav-accent)' }} />
              </div>
            )}
          </div>

          <nav className="flex-1 px-3 py-2 overflow-y-auto custom-scrollbar" aria-label="Primary navigation">
            {NAV_SECTIONS.map((section, sectionIdx) => (
              <div key={section.label} className="mb-1">
                {sectionIdx > 0 && showExpanded ? <div className="pd-sidebar-section-divider" aria-hidden="true" /> : null}
                {showExpanded && (
                  <div className="text-[9px] uppercase tracking-[0.14em] font-semibold px-2 pt-3 pb-1"
                    style={{ color: 'var(--pd-subtle)' }}>{section.label}</div>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <SidebarNavItem
                      key={item.path}
                      item={item}
                      active={isActive(item.path)}
                      expanded={showExpanded}
                      count={item.countKey ? (counts[item.countKey] || 0) : 0}
                      isUrgent={item.urgentKey ? (counts[item.urgentKey] || 0) > 0 : false}
                      onClick={() => handleNavigate(item.path)}
                    />
                  ))}
                </div>
              </div>
            ))}

            <SidebarMoreSection
              moreOpen={moreOpen}
              onToggle={toggleMore}
              counts={counts}
              expanded={showExpanded}
              isActive={isActive}
              onNavigate={handleNavigate}
            />

            {/* Saved views — user-curated quick filter snapshots */}
            <SidebarSavedViews expanded={showExpanded} />

            <SidebarBusinessFilter
              businesses={businesses}
              activeBusiness={activeBusiness}
              setActiveBusiness={setActiveBusiness}
              expanded={showExpanded}
            />
          </nav>

          <div className="px-3 pb-3">
            {/* Sidebar toggle — positioned in the footer for a clean brand area */}
            {!isMobile ? (
              showExpanded ? (
                <button
                  data-testid="sidebar-toggle-button"
                  onClick={() => toggleExpanded(false)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl mb-2 transition-colors"
                  style={{ color: 'var(--pd-subtle)', background: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--pd-panel-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  title="Collapse sidebar"
                >
                  <PanelLeftClose className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-medium tracking-wide uppercase sidebar-label">Collapse</span>
                </button>
              ) : (
                <button
                  data-testid="sidebar-toggle-button"
                  onClick={() => toggleExpanded(true)}
                  className="w-full flex items-center justify-center py-2.5 rounded-xl mb-2 transition-colors"
                  style={{ color: 'var(--pd-subtle)', background: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--pd-panel-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  title="Expand sidebar"
                >
                  <PanelLeftOpen className="w-4 h-4" />
                </button>
              )
            ) : null}

            <SessionHealthBadge sessionMeta={sessionMeta} lastSyncTime={lastSyncTime} expanded={showExpanded} />
            <DraftIndicator expanded={showExpanded} />
            <button data-testid="nav-settings" onClick={() => handleNavigate('/settings')} className="sidebar-nav-item pd-sidebar-nav-item" style={{ minHeight: 52, justifyContent: showExpanded ? 'flex-start' : 'center', padding: showExpanded ? '0 10px' : '0' }}>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"><Settings className="w-[18px] h-[18px]" strokeWidth={1.9} /></div>
              {showExpanded ? (
                <div className="sidebar-label flex-1 text-left">
                  <div className="text-[13px] font-semibold">Preferences</div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>Themes, shortcuts, account</div>
                </div>
              ) : null}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

/**
 * Wrap in `React.memo` so the sidebar skips re-renders when
 * `AppShellLayout` state changes that aren't relevant to it
 * (notesOpen, commandBarOpen, searchOpen, focusTask, etc.). The
 * parent uses `useCallback` for the two function props so the
 * default shallow-equal comparator is enough — no custom comparator
 * needed. Context-driven re-renders (auth, business, theme) still
 * propagate correctly because `memo` doesn't intercept context
 * subscriptions.
 */
export default memo(Sidebar);
